<template>
    <div>
        <ChecklistDailyView v-if="selectedRecord" :selected-date="selectedRecord.checkDate" :view-only="true"
            :is-history-view="true" @back="selectedRecord = null" />
        <div v-else class="past-records-page">
            <div class="page-header">
                <h1>Past Records</h1>
                <p>Click any date to view completed checklists</p>
            </div>

            <!-- Filters -->
            <div class="filter-card">
                <div class="filter-header">
                    <div class="filter-title">
                        <i class="pi pi-filter"></i>
                        <span>Filters</span>
                    </div>

                    <button class="toggle-btn" @click="showFilters = !showFilters">
                        {{ showFilters ? 'Hide' : 'Show' }}
                    </button>
                </div>

                <div v-if="showFilters" class="filter-body">
                    <div class="filter-section">
                        <label class="section-label">Status</label>

                        <div class="status-tabs">
                            <button class="status-tab" :class="{ active: filters.status === '' }"
                                @click="setStatus('')">
                                All
                            </button>

                            <button class="status-tab" :class="{ active: filters.status === '1' }"
                                @click="setStatus('1')">
                                Completed
                            </button>

                            <button class="status-tab" :class="{ active: filters.status === '0' }"
                                @click="setStatus('0')">
                                Not Completed
                            </button>
                        </div>
                    </div>

                    <div class="filter-section">
                        <label class="section-label">Date Range</label>

                        <div class="date-grid">
                            <div class="date-field">
                                <label>From Date</label>
                                <el-date-picker v-model="filters.startDate" type="date" placeholder="Start date"
                                    format="dd/MM/yyyy" value-format="yyyy-MM-dd" class="date-picker-full" clearable />
                            </div>

                            <div class="date-field">
                                <label>To Date</label>
                                <el-date-picker v-model="filters.endDate" type="date" placeholder="End date"
                                    format="dd/MM/yyyy" value-format="yyyy-MM-dd" class="date-picker-full" clearable />
                            </div>
                        </div>
                    </div>

                    <div class="filter-actions">
                        <button class="secondary-btn" @click="resetFilters">Reset</button>
                        <button class="primary-btn" @click="applyFilters">Apply</button>
                    </div>
                </div>
            </div>

            <!-- List -->
            <div v-if="loading" class="state-box">Loading records...</div>

            <div v-else-if="historyList.length === 0" class="state-box">
                No records found
            </div>

            <div v-else class="history-list">
                <div v-for="item in historyList" :key="item.checkDate" class="history-card" @click="openRecord(item)">
                    <div class="history-left">
                        <div class="icon-box">
                            <i class="pi pi-calendar"></i>
                        </div>

                        <div class="history-info">
                            <h3>{{ formatDisplayDate(item.checkDate) }}</h3>
                            <p>{{ item.completedCount }} of 30 checklists completed</p>
                        </div>
                    </div>

                    <div class="history-right">
                        <span class="status-badge" :class="getBadgeClass(item)">
                            {{ getStatusText(item) }}
                        </span>

                        <i class="pi pi-chevron-right arrow-icon"></i>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getChecklistHistory } from '@/services/user';
import 'primeicons/primeicons.css'
import ChecklistDailyView from './ChecklistDailyView.vue';

export default {
    name: 'PastRecords',
    components: {
        ChecklistDailyView
    },
    data() {
        return {
            showFilters: true,
            loading: false,
            historyList: [],
            total: 0,
            filters: {
                startDate: '',
                endDate: '',
                pageNum: 1,
                pageSize: 10,
                status: ''
            },
            selectedRecord: null
        }
    },
    mounted() {
        this.fetchChecklistHistory()
    },
    methods: {
        async fetchChecklistHistory() {
            this.loading = true

            try {
                const params = {
                    startDate: this.filters.startDate,
                    endDate: this.filters.endDate,
                    pageNum: this.filters.pageNum,
                    pageSize: this.filters.pageSize
                }

                if (this.filters.status !== '') {
                    params.status = this.filters.status
                }

                const res = await getChecklistHistory(params)
                const payload = ((res || {}).data || {})

                this.total = payload.total || 0
                this.historyList = payload.rows || []
            } catch (error) {
                console.error('getChecklistHistory error:', error)
                this.total = 0
                this.historyList = []
            } finally {
                this.loading = false
                this.emitPaginationState()
            }
        },

        emitPaginationState() {
            this.$emit('history-page-state', {
                total: this.total,
                pageNum: this.filters.pageNum,
                pageSize: this.filters.pageSize,
                loading: this.loading
            })
        },

        setPage(pageNum) {
            this.filters.pageNum = pageNum
            this.fetchChecklistHistory()
        },

        setPageSize(pageSize) {
            this.filters.pageSize = pageSize
            this.filters.pageNum = 1
            this.fetchChecklistHistory()
        },

        setStatus(value) {
            this.filters.status = value
        },

        resetFilters() {
            this.filters = {
                startDate: '',
                endDate: '',
                pageNum: 1,
                pageSize: 10,
                status: ''
            }

            this.fetchChecklistHistory()
        },

        getStatusText(item) {
            const dayDone = Number(item.daySignStatus) === 1
            const nightDone = Number(item.nightSignStatus) === 1

            if (dayDone && nightDone) {
                return 'Completed'
            }

            return 'Not Completed'
        },

        getBadgeClass(item) {
            const dayDone = Number(item.daySignStatus) === 1
            const nightDone = Number(item.nightSignStatus) === 1

            if (dayDone && nightDone) {
                return 'completed'
            }

            return 'not-completed'
        },

        formatDisplayDate(dateStr) {
            const date = new Date(dateStr)

            return date.toLocaleDateString('en-GB', {
                weekday: 'long',
                day: '2-digit',
                month: 'long',
                year: 'numeric'
            })
        },

        openRecord(item) {
            this.selectedRecord = item
        },

        applyFilters() {
            this.filters.pageNum = 1
            this.fetchChecklistHistory()
        },
    }
}
</script>

<style scoped lang="scss">
.past-records-page {
    background: #f6f8fb;
    padding: 16px;
}

/* HEADER */

.page-header {
    margin-bottom: 20px;
}

.page-header h1 {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: #161b26;
}

.page-header p {
    margin-top: 4px;
    font-size: 12px;
    color: #61728d;
}

/* FILTER CARD */

.filter-card {
    background: white;
    border: 1px solid #dfe4ec;
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 24px;
}

.filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.filter-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
    font-weight: 600;
}

.filter-title i {
    color: #1389e7;
    font-size: 18px;
}

.toggle-btn {
    border: none;
    background: transparent;
    color: #1389e7;
    font-size: 14px;
    cursor: pointer;
}

/* FILTER BODY */

.filter-body {
    margin-top: 18px;
}

.section-label {
    font-weight: 600;
    color: #61728d;
    margin-bottom: 8px;
    display: block;
}

/* STATUS BUTTONS */

.status-tabs {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 16px;
}

.status-tab {
    height: 40px;
    border: none;
    border-radius: 10px;
    background: #eef1f6;
    color: #38455b;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.status-tab.active {
    background: #377ded;
    color: white;
}

/* DATE RANGE */

.date-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.date-field {
    display: flex;
    flex-direction: column;
}

.date-field label {
    font-size: 13px;
    color: #61728d;
    margin-bottom: 4px;
}

.date-picker-full {
    width: 100%;
}

::v-deep(.date-picker-full .el-input__wrapper) {
    min-height: 36px;
    box-shadow: 0 0 0 1px #cfd6e0 inset !important;
    background: #fff;
}

::v-deep(.date-picker-full .el-input__inner) {
    font-size: 12px;
    color: #161b26;
    border-radius: 10px;
}

::v-deep(.date-picker-full .el-input__prefix),
::v-deep(.date-picker-full .el-input__suffix) {
    color: #8b97aa;
}

/* FILTER ACTIONS */

.filter-actions {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.primary-btn,
.secondary-btn {
    height: 36px;
    padding: 0 14px;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
}

.primary-btn {
    background: #377ded;
    border: none;
    color: white;
}

.secondary-btn {
    border: 1px solid #d4dae3;
    background: white;
}

/* HISTORY LIST */

.history-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

/* HISTORY CARD */

.history-card {
    background: white;
    border: 1px solid #e1e6ef;
    border-radius: 14px;
    padding: 14px 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
}

.history-card:hover {
    background: #f9fbff;
}

/* LEFT SIDE */

.history-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.icon-box {
    width: 40px;
    height: 40px;
    background: #f3f4f6;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.icon-box i {
    font-size: 18px;
    color: #556274;
}

.history-info h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
}

.history-info p {
    margin: 2px 0 0;
    font-size: 13px;
    color: #6d7c93;
}

/* RIGHT SIDE */

.history-right {
    display: flex;
    align-items: center;
    gap: 10px;
}

/* STATUS BADGE */

.status-badge {
    height: 30px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
}

.status-badge.completed {
    background: #edf9f3;
    border: 1px solid #39b56a;
    color: #23884d;
}

.status-badge.not-completed {
    background: #fff4e9;
    border: 1px solid #f3a347;
    color: #d25400;
}

/* ARROW */

.arrow-icon {
    font-size: 16px;
    color: #98a3b3;
}

/* EMPTY / LOADING */

.state-box {
    background: white;
    border: 1px solid #e1e6ef;
    border-radius: 14px;
    padding: 20px;
    text-align: center;
    color: #6d7c93;
}

/* MOBILE */

@media (max-width: 768px) {

    .date-grid {
        grid-template-columns: 1fr;
    }

    .status-tabs {
        grid-template-columns: 1fr;
    }

    .history-card {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }

    .history-right {
        width: 100%;
        justify-content: space-between;
    }
}
</style>